(() => {
  const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const initialize = (scope) => {
    const roots = scope.matches?.('[data-lc-catalog]') ? [scope] : scope.querySelectorAll('[data-lc-catalog]');
    roots.forEach((root) => {
      if (root.dataset.catalogReady) return;
      root.dataset.catalogReady = 'true';
      let labels = {};
      try { labels = JSON.parse(root.querySelector('[data-catalog-labels]')?.textContent || '{}'); } catch {}
      const controls = root.querySelector('[data-catalog-controls]');
      const search = root.querySelector('[data-catalog-search]');
      const sort = root.querySelector('[data-catalog-sort]');
      const filters = [...root.querySelectorAll('[data-catalog-material]')];
      const grid = root.querySelector('[data-catalog-grid]');
      const cards = [...root.querySelectorAll('[data-catalog-card]')];
      const empty = root.querySelector('[data-catalog-empty]');
      const status = root.querySelector('[data-catalog-status]');
      let material = 'all';
      const filter = () => {
        const query = normalize(search.value);
        const terms = query.split(/\s+/).filter(Boolean);
        let count = 0;
        cards.forEach((card) => {
          const content = normalize(`${card.dataset.title} ${card.dataset.material}`);
          const show = (material === 'all' || card.dataset.material === material) && terms.every((term) => content.includes(term));
          card.hidden = !show;
          if (show) count += 1;
        });
        filters.forEach((button) => {
          const active = button.dataset.catalogMaterial === material;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        empty.hidden = count > 0;
        status.hidden = !query && material === 'all';
        status.textContent = (labels.resultsPattern || '[count] designs found.').replaceAll('[count]', String(count));
      };
      const orderCards = () => {
        const mode = sort.value;
        const sorted = [...cards].sort((a, b) => {
          if (mode === 'price-ascending') return Number(a.dataset.price) - Number(b.dataset.price);
          if (mode === 'price-descending') return Number(b.dataset.price) - Number(a.dataset.price);
          if (mode === 'title-ascending') return a.dataset.title.localeCompare(b.dataset.title, undefined, { sensitivity: 'base' });
          if (mode === 'title-descending') return b.dataset.title.localeCompare(a.dataset.title, undefined, { sensitivity: 'base' });
          return Number(a.dataset.featured) - Number(b.dataset.featured);
        });
        sorted.forEach((card) => grid.append(card));
      };
      search.addEventListener('input', filter);
      filters.forEach((button) => button.addEventListener('click', () => {
        material = button.dataset.catalogMaterial;
        filter();
      }));
      sort.addEventListener('change', orderCards);
      root.querySelector('[data-catalog-reset]').addEventListener('click', () => {
        material = 'all';
        search.value = '';
        filter();
        search.focus();
      });
      root.querySelectorAll('[data-catalog-form]').forEach((form) => {
        const input = form.querySelector('[data-catalog-quantity-input]');
        const quantityButtons = [...form.querySelectorAll('[data-catalog-quantity]')];
        const card = form.closest('[data-catalog-card]');
        const summary = form.querySelector('[data-catalog-order-summary]');
        const packCount = Number(form.dataset.packCount) || 1;
        const unitPrice = Number(form.dataset.unitPrice);
        const currency = document.documentElement.dataset.currency || 'USD';
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });
        const minimum = Number(input.min) || 1;
        const step = Number(input.step) || 1;
        const maximum = input.max ? Number(input.max) : Infinity;
        const updateQuantityButtons = () => {
          const value = Number(input.value);
          if (summary) {
            const valid = Number.isInteger(value) && value >= minimum && value <= maximum && (value - minimum) % step === 0 && Number.isFinite(unitPrice);
            summary.hidden = !valid;
            if (valid) {
              const patches = value * packCount;
              const unit = value === 1 ? (labels.packUnit || 'pack') : (labels.packsLabel || 'Packs').toLowerCase();
              const patchUnit = patches === 1 ? (labels.patchUnit || 'patch') : (labels.patchesLabel || 'Patches').toLowerCase();
              const pattern = card.dataset.material === 'pvc' ? (labels.packSummaryPattern || '[quantity] [unit] = [patches] [patchUnit] · [total]') : (labels.patchSummaryPattern || '[patches] [patchUnit] · [total]');
              summary.textContent = pattern.replaceAll('[quantity]', String(value)).replaceAll('[unit]', unit).replaceAll('[patches]', String(patches)).replaceAll('[patchUnit]', patchUnit).replaceAll('[total]', money.format(unitPrice * value / 100));
            }
          }
          quantityButtons.forEach((button) => {
            button.disabled = button.dataset.catalogQuantity === 'decrease' ? value <= minimum : value + step > maximum;
          });
        };
        quantityButtons.forEach((button) => button.addEventListener('click', () => {
          const current = Number(input.value);
          const valid = Number.isFinite(current) && current >= minimum ? current : minimum;
          const aligned = minimum + Math.round((valid - minimum) / step) * step;
          input.value = String(Math.min(maximum, Math.max(minimum, aligned + (button.dataset.catalogQuantity === 'increase' ? step : -step))));
          updateQuantityButtons();
        }));
        input.addEventListener('input', updateQuantityButtons);
        input.addEventListener('change', updateQuantityButtons);
        updateQuantityButtons();
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (!form.reportValidity()) return;
          const card = form.closest('[data-catalog-card]');
          const message = card.querySelector('[data-catalog-message]');
          const messageText = card.querySelector('[data-catalog-message-text]');
          const cartLink = card.querySelector('[data-catalog-view-cart]');
          const addButton = form.querySelector('[data-catalog-add]');
          const buttonText = addButton.querySelector('span');
          if (addButton.disabled) return;
          addButton.disabled = true;
          buttonText.textContent = labels.addingLabel || 'Adding...';
          message.hidden = true;
          cartLink.hidden = true;
          try {
            const response = await fetch(root.dataset.cartAddUrl, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: form.querySelector('[name="id"]').value, quantity: Number(input.value) }] })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.description || result.message || 'We could not add this design. Please try again.');
            root.dispatchEvent(new CustomEvent('lc:journey', { bubbles: true, detail: { event: 'premade_cart_added', journey: 'premade' } }));
            message.classList.remove('is-error');
            message.setAttribute('role', 'status');
            const amount = Number(input.value);
            const unit = card.dataset.material === 'pvc' ? (amount === 1 ? labels.packUnit || 'pack' : (labels.packsLabel || 'Packs').toLowerCase()) : (amount === 1 ? labels.patchUnit || 'patch' : (labels.patchesLabel || 'Patches').toLowerCase());
            messageText.textContent = (labels.addedPattern || '[quantity] [unit] added to your cart.').replaceAll('[quantity]', String(amount)).replaceAll('[unit]', unit);
            cartLink.hidden = false;
            message.hidden = false;
            try {
              const cartResponse = await fetch(root.dataset.cartJsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
              if (cartResponse.ok) {
                const cart = await cartResponse.json();
                document.dispatchEvent(new CustomEvent('lc:cart-updated', { detail: { cart } }));
              }
            } catch {}
          } catch (error) {
            message.classList.add('is-error');
            message.setAttribute('role', 'alert');
            messageText.textContent = error.message || 'Something went wrong. Please try again.';
            message.hidden = false;
          } finally {
            addButton.disabled = false;
            buttonText.textContent = labels.addLabel || 'Add to cart';
          }
        });
      });
      if (cards.length) controls.hidden = false;
      filter();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document));
  else initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
